import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Page } from '../../layout';
import { Alert, Icon, Button } from '../../components';
import { formatBRL } from '../../utils/Formats';
import { Api, getSession } from '../../api/Assinafy';
import { isFreePlan, isPlanChangeOptionAllowed } from '../Billing/planOptions';

import './styles.scss';

const Plans = () => {
  const location = useLocation()
  const { state } = location
  const [plansList, setPlansList] = useState([])
  const [billingInterval, setBillingInterval] = useState('Monthly')
  const [currentPlan, setCurrentPlan] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showAlert, setShowAlert] = useState(!!state?.alert)
  const [selectionError, setSelectionError] = useState('')
  const [selectingPlanId, setSelectingPlanId] = useState(null)
  const navigate = useNavigate()

  const alertClose = () => {
    setShowAlert(false)
    window.history.replaceState(Object.assign({ ...state }, { alert: {} }), '')
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const session = getSession()
        const currentAccount = session?.accounts?.[0]

        const plansResponse = await Api('plan/index')()
        if (plansResponse?.status === 200 && plansResponse?.data?.data) {
          setPlansList(plansResponse.data.data)
        }

        if (currentAccount?.id) {
          try {
            const subscriptionResponse = await Api('subscription/view')(currentAccount.id)
            if (subscriptionResponse?.status === 200 && subscriptionResponse?.data?.data) {
              const subscription = subscriptionResponse.data.data
              setCurrentPlan(subscription)
              if ((subscription.billing_interval || subscription.plan?.billing_interval) === 'Yearly') {
                setBillingInterval('Yearly')
              }
            }
          } catch (error) {
            console.error('Erro ao buscar plano atual:', error)
          }
        }
      } catch (error) {
        console.error('Erro ao buscar planos:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    const payload = location.state?.pendingActionResolve
    if (!payload?.actionId) {
      return undefined
    }

    const accountId = payload.accountId ?? getSession()?.accounts?.[0]?.id
    if (!accountId) {
      return undefined
    }

    const resolveApi = Api('account/pendingActionResolve')
    if (!resolveApi) {
      return undefined
    }

    let cancelled = false
    resolveApi(accountId, payload.actionId)
      .catch(() => {})
      .finally(() => {
        if (cancelled) {
          return
        }
        const next = { ...(location.state || {}) }
        delete next.pendingActionResolve
        navigate(location.pathname, { replace: true, state: next })
      })

    return () => {
      cancelled = true
    }
  }, [location.state, location.pathname, navigate])

  const visiblePlans = ['free', 'standard', 'pro']
    .map((tier) => plansList.find((plan) => (
      getPlanTier(plan) === tier &&
      (tier === 'free' || plan.billing_interval === billingInterval) &&
      isPlanChangeOptionAllowed(currentPlan, plan)
    )))
    .filter(Boolean)

  const handlePlanSelect = async (plan) => {
    setSelectionError('')
    setSelectingPlanId(plan.id)

    try {
      const response = await Api('user/updatePlan')(plan.name.trim().toLowerCase())

      if (response?.status !== 200) {
        throw new Error(response?.data?.message || 'Não foi possível salvar sua escolha de plano.')
      }

      setSelectingPlanId(null)

      const isCurrentFreePlan = isFreePlan(plan) && (
        !currentPlan?.plan || isFreePlan(currentPlan.plan)
      )

      if (isCurrentFreePlan) {
        navigate('/dashboard')
        return
      }

      navigate("/dashboard/workspace/billing/changeplan", {
        state: {
          selectedPlan: plan,
          currentPlan,
          fromPlans: true,
        },
      })
    } catch (error) {
      setSelectionError(
        error?.response?.data?.message ||
        error?.message ||
        'Não foi possível salvar sua escolha de plano. Tente novamente.'
      )
      setSelectingPlanId(null)
    }
  }

  return (
    <Page className="page-plans"
      title="Escolha o melhor plano para você"
      opening="Escolha o plano que melhor atende às necessidades de sua empresa."
    >
      {showAlert && state?.alert &&
        <Alert variant={state.alert.type} onClose={alertClose}>
          {state.alert.message}
        </Alert>
      }
      {currentPlan?.pending_payment && (
        <Alert variant="warning">
          Você já tem uma compra de plano aguardando pagamento. Abra as configurações do workspace para pagar ou cancelar o PIX antes de escolher outro plano.
        </Alert>
      )}
      {selectionError &&
        <Alert variant="warning" onClose={() => setSelectionError('')}>
          {selectionError}
        </Alert>
      }

      {loading ? (
        <div className="text-center p-5">Carregando planos...</div>
      ) : (
        <>
          <div className="plans-billing-picker">
            <div className="plans-interval" role="group" aria-label="Periodicidade da cobrança">
            <Button
              className={billingInterval === 'Monthly' ? 'active' : ''}
              onClick={() => setBillingInterval('Monthly')}
              aria-pressed={billingInterval === 'Monthly'}
            >
              Mensal
            </Button>
            <Button
              className={billingInterval === 'Yearly' ? 'active' : ''}
              onClick={() => setBillingInterval('Yearly')}
              aria-pressed={billingInterval === 'Yearly'}
            >
              Anual <span>2 meses grátis</span>
            </Button>
            </div>
          </div>
          <List>
          {visiblePlans.map((item) =>
            <Item
              key={item.id}
              {...item}
              currentPlan={currentPlan}
              disabled={selectingPlanId !== null}
              hasPendingPayment={!!currentPlan?.pending_payment}
              loading={selectingPlanId === item.id}
              onSelect={handlePlanSelect}
            />
          )}
          {!visiblePlans.length && (
            <p className="plans-billing-caption">Não há upgrades disponíveis nesta periodicidade.</p>
          )}
          </List>
        </>
      )}

    </Page>
  );
};

const List = (props) => {
  return (
    <div {...props} className="plans-list" />
  );
};

const PLAN_FEATURES = {
  free: [
    'API, SDKs, CLI e MCP Server liberados',
    'Usuários e signatários ilimitados',
    'Token por e-mail, sem custo',
    'Backup de 90 dias',
  ],
  standard: [
    'Tudo do Basic',
    'WhatsApp, biometria, validação e A1',
    'Webhooks, sandbox e templates via API',
    'Backup de 180 dias',
  ],
  pro: [
    'Tudo do Standard',
    'Logo e cores da sua marca',
    'Trilha de auditoria completa',
    'Backup vitalício',
  ],
}

const getPlanTier = (plan) => {
  const name = String(plan?.name || '').toLowerCase()
  if (isFreePlan(plan) || name.includes('basic')) return 'free'
  if (name.includes('standard')) return 'standard'
  if (name.includes('pro')) return 'pro'
  return null
}

const Item = (props) => {
  const {
    name,
    price,
    max_documents,
    extra_credit_price,
    id,
    billing_interval,
    currentPlan,
    disabled,
    hasPendingPayment,
    loading,
    onSelect,
  } = props;
  const tier = getPlanTier(props)
  const free = tier === 'free'
  const isCurrentFreePlan = free && (!currentPlan?.plan || isFreePlan(currentPlan.plan))
  const isCurrentPlan = currentPlan?.plan?.id === id || isCurrentFreePlan
  const displayName = tier === 'free'
    ? 'Basic'
    : name.replace(/\s+(anual|annual)$/i, '')
  const isYearly = billing_interval === 'Yearly' && !free
  const displayedPrice = isYearly ? Number(price) / 12 : Number(price)
  const features = PLAN_FEATURES[tier] || []

  const handleClick = () => {
    onSelect({
      id,
      name,
      price,
      max_documents,
      extra_credit_price,
      billing_interval
    })
  }

  return (
    <article className={`plan-item plan-item-${tier}${tier === 'standard' ? ' plan-item-featured' : ''}`}>
      {tier === 'standard' && <span className="plan-popular">Mais escolhido</span>}
      <div className="plan-meta">
        {isCurrentPlan && <strong className="current">Seu plano</strong>}
      </div>
      <div className="plan-header">
        <h2 className="plan-title">
          {displayName}
        </h2>
        <div className="plan-price">
          {free ? 'Grátis' : formatBRL(displayedPrice)}
        </div>
        <p className="plan-price-caption">
          {free
            ? 'Sem cartão'
            : isYearly
              ? `${formatBRL(price)}/ano · só no PIX`
              : 'por mês'}
        </p>
      </div>
      <div className="plan-body">
        <div className="plan-docs-total">
          {max_documents} documentos
          <span>/mês</span>
        </div>
        <ul className="plan-features">
          {features.map((feature) => (
            <li key={feature}>
              <Icon id="icon-check" size="18" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="plan-footer">
        <Button
          variant={tier === 'standard' ? 'primary' : 'info'}
          onClick={handleClick}
          className="btn plan-cta"
          disabled={disabled || hasPendingPayment || (isCurrentPlan && !free)}
          loading={loading}
        >
          {hasPendingPayment
            ? 'Pagamento pendente'
            : isCurrentPlan && free
              ? 'Continuar com o plano grátis'
              : isCurrentPlan
                ? 'Este é seu plano'
                : free
                  ? 'Escolher plano grátis'
                  : 'Assinar agora'}
        </Button>
      </div>
    </article>
  );
};

export default Plans;
